import React, { useState } from "react";
import { Button, Modal, Alert } from "react-bootstrap";
import { useAppDispatch } from "../../app/hooks";
import {
  sendOrderEmail,
  getOrderEmailHistory,
  getPriceProposalPreview,
} from "../../app/services/orderService";
import EmailHistoryAccordion from "../email/emailHistoryList";
import { EmailHistory } from "../../models/responses/emailHistory";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";

interface FleetPriceProposalProps {
  instance: any;
  account: any;
  orderId: string | undefined;
  emailHistory: EmailHistory[] | null;
  isWaitingSendOrderEmailResponse: boolean;
  isWaitingPriceProposalPreviewResponse: boolean;
}

const FleetPriceProposal: React.FC<FleetPriceProposalProps> = ({
  instance,
  account,
  orderId,
  emailHistory,
  isWaitingSendOrderEmailResponse,
  isWaitingPriceProposalPreviewResponse,
}) => {
  const dispatch = useAppDispatch();
  const [showEmailSuccess, setShowEmailSuccess] = useState(false);
  const [showEmailError, setShowEmailError] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);

  const hasUpdatedStock = useSelector(
    (state: RootState) => state.order.hasUpdatedStock,
  );

  const onEmailSuccess = (success: boolean | null) => {
    if (success) {
      setShowEmailSuccess(true);
      setShowEmailError(false);
      setTimeout(() => setShowEmailSuccess(false), 4000);
    } else {
      setShowEmailError(true);
      setShowEmailSuccess(false);
      setTimeout(() => setShowEmailError(false), 4000);
    }
  };

  const handleSendPriceProposal = async () => {
    await dispatch(sendOrderEmail(instance, account, orderId, onEmailSuccess));
    dispatch(getOrderEmailHistory(instance, account, orderId));
  };

  const handleResendPriceProposal = () => {
    setShowResendModal(true);
  };

  const confirmResendPriceProposal = async () => {
    setShowResendModal(false);
    await dispatch(sendOrderEmail(instance, account, orderId, onEmailSuccess));
    dispatch(getOrderEmailHistory(instance, account, orderId));
  };

  const cancelResendPriceProposal = () => {
    setShowResendModal(false);
  };

  const handlePriceProposalPreview = async () => {
    await dispatch(getPriceProposalPreview(instance, account, orderId));
  };

  return (
    <div className="mt-3">
      <Button
        variant="primary"
        onClick={handlePriceProposalPreview}
        disabled={isWaitingPriceProposalPreviewResponse}
        className="me-3"
      >
        Förhandsgranska prisförslag 
      </Button>
      {(!emailHistory || emailHistory.length === 0) && (
        <>
          {hasUpdatedStock ? (
            <Button
              variant="primary"
              onClick={handleSendPriceProposal}
              disabled={isWaitingSendOrderEmailResponse}
            >
              Skicka prisförslag till Postnord
            </Button>
          ) : (
            <Button variant="primary" disabled={true} aria-readonly={true}>
              Hämta lagersaldo innan du kan skicka prisförslag.
            </Button>
          )}
        </>
      )}
      {emailHistory && emailHistory.length > 0 && (
        <>
          {hasUpdatedStock ? (
            <Button
              variant="primary"
              onClick={handleResendPriceProposal}
              disabled={isWaitingSendOrderEmailResponse}
            >
              Skicka prisförslag igen till Postnord
            </Button>
          ) : (
            <Button variant="primary" disabled={true} aria-readonly={true}>
              Hämta lagersaldo innan du kan skicka prisförslag.
            </Button>
          )}
          <Modal show={showResendModal} onHide={cancelResendPriceProposal}>
            <Modal.Header closeButton>
              <Modal.Title>Skicka prisförslag igen</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              Det finns redan skickade prisförslag för denna order.
              <br />
              Är du säker på att du vill skicka prisförslaget igen till
              Postnord?
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={cancelResendPriceProposal}>
                Avbryt
              </Button>
              <Button variant="primary" onClick={confirmResendPriceProposal}>
                Skicka ändå
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
      <Alert
        key="email-success"
        variant="success"
        show={showEmailSuccess}
        className="mt-3"
      >
        <p>Prisförslagsmail skickat!</p>
      </Alert>
      <Alert
        key="email-error"
        variant="danger"
        show={showEmailError}
        className="mt-3"
      >
        <p>
          Något gick fel vid skickandet av prisförslagsmail, vänligen försök
          igen.
        </p>
      </Alert>
      <div className="mt-3">
        <EmailHistoryAccordion emailHistory={emailHistory} />
      </div>
    </div>
  );
};

export default FleetPriceProposal;
